import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import { getTokens } from "../../styles/tokens";
import SlaChip, { useTicketSla } from "../SlaChip";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";

import GroupIcon from "@material-ui/icons/Group";
import ConnectionIcon from "../ConnectionIcon";
import AcceptTicketWithouSelectQueue from "../AcceptTicketWithoutQueueModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import ShowTicketOpen from "../ShowTicketOpenModal";
import FinalizacaoVendaModal from "../FinalizacaoVendaModal";
import { isNil } from "lodash";
import { toast } from "react-toastify";
import { Done, HighlightOff, SwapHoriz, Add } from "@material-ui/icons";
import VisibilityIcon from "@material-ui/icons/Visibility"; // Ícone de spy
import useCompanySettings from "../../hooks/useSettings/companySettings";
import NewTicketModal from "../NewTicketModal";
import MuteDialog from "../MuteDialog";
import MutedIcon from "../MuteDialog/MutedIcon";
import MuteMenuItem from "../MuteDialog/MuteMenuItem";
import { useMutes } from "../../context/Mutes/MutesContext";
import {
  Avatar,
  ListItem,
  Menu,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  DialogContent,
} from "@material-ui/core";

/* Apresentação (F4 — ltenetfast): item de 72px, avatar 40 com indicador de canal,
 * nome 14/600 + horário 12 à direita, última mensagem 13 em 1 linha, linha de
 * badges 11px (fila = ponto colorido + texto), não lidas em pill semantic.error.
 * Cores de fila/tag/conexão vêm dos dados; o resto vem dos tokens. */
const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  const isDark = theme.palette.type === "dark";
  // Lista de conversas no padrão do WhatsApp Web: linhas planas, selo verde de não lidas.
  const wa = isDark
    ? { hover: "#202c33", selected: "#2a3942", unread: "#00a884", unreadText: "#111b21" }
    : { hover: "#f5f6f6", selected: "#f0f2f5", unread: "#25d366", unreadText: "#ffffff" };
  const ellipsis = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };
  return {
    ticket: {
      position: "relative",
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      width: "100%",
      minHeight: 74,
      margin: 0,
      padding: "10px 14px 9px",
      border: "1px solid transparent",
      borderBottomColor: t.line,
      borderRadius: 0,
      boxSizing: "border-box",
      backgroundColor: "transparent",
      boxShadow: "none",
      transition: "background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
      "&:hover": {
        zIndex: 1,
        backgroundColor: wa.hover,
        borderColor: "transparent",
        borderBottomColor: t.line,
        boxShadow: "none",
      },
      "&.Mui-selected, &.Mui-selected:hover": {
        background: wa.selected,
        borderColor: "transparent",
        borderBottomColor: t.line,
        borderRadius: 0,
        boxShadow: "none",
      },
      "&:hover $actions, &:focus-within $actions, &.Mui-selected $actions": {
        opacity: 1,
        transform: "translateX(0)",
      },
    },
    pendingTicket: {
      cursor: "unset",
    },
    slaBreached: {
      borderColor: `${t.semantic.error}55`,
      boxShadow: `inset 3px 0 0 ${t.semantic.error}`,
      "&.Mui-selected, &.Mui-selected:hover": {
        boxShadow: `inset 3px 0 0 ${t.semantic.error}`,
      },
    },

    avatarWrap: {
      position: "relative",
      flex: "none",
      width: 46,
      height: 46,
      marginTop: 1,
    },
    avatar: {
      width: 46,
      height: 46,
      border: 0,
      backgroundColor: t.accentTint.soft,
      color: t.brand,
      boxShadow: "none",
      cursor: "pointer",
      "&:hover": {
        opacity: 0.85,
      },
    },
    channelDot: {
      position: "absolute",
      right: -3,
      bottom: -3,
      width: 16,
      height: 16,
      borderRadius: "50%",
      backgroundColor: t.paper,
      boxShadow: `0 0 0 2px ${t.paper}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      pointerEvents: "none",
      "& svg": {
        fontSize: 12,
        marginBottom: "0 !important",
      },
    },

    body: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 2,
    },
    row: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      minWidth: 0,
    },
    name: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      alignItems: "center",
      gap: 4,
      fontSize: 15,
      fontWeight: 600,
      lineHeight: "20px",
      color: t.text,
    },
    nameText: {
      ...ellipsis,
    },
    groupIcon: {
      flex: "none",
      fontSize: 16,
      color: t.textMuted,
    },
    time: {
      flex: "none",
      fontSize: 11,
      lineHeight: "16px",
      color: t.textMuted,
      fontVariantNumeric: "tabular-nums",
    },
    timeUnread: {
      color: wa.unread,
      fontWeight: 700,
    },
    lastMessage: {
      flex: 1,
      minWidth: 0,
      fontSize: 12.5,
      lineHeight: "16px",
      color: t.textSecondary,
      ...ellipsis,
      "& p": {
        margin: 0,
        display: "inline",
      },
    },
    lastMessageUnread: {
      color: t.text,
      fontWeight: 500,
    },
    unread: {
      flex: "none",
      minWidth: 20,
      height: 20,
      padding: "0 6px",
      borderRadius: 10,
      backgroundColor: wa.unread,
      color: wa.unreadText,
      fontSize: 11.5,
      fontWeight: 700,
      lineHeight: "20px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontVariantNumeric: "tabular-nums",
    },
    unreadMuted: {
      backgroundColor: isDark ? "#8696a0" : "#9aa5ab",
      color: isDark ? "#111b21" : "#ffffff",
    },
    mutedIcon: {
      fontSize: 14,
      color: t.textMuted,
    },

    meta: {
      flex: 1,
      minWidth: 0,
      minHeight: 18,
      display: "flex",
      alignItems: "center",
      gap: 4,
      overflow: "hidden",
      paddingRight: 26,
    },
    badge: {
      flex: "none",
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      height: 18,
      maxWidth: 92,
      padding: "0 6px",
      borderRadius: 9,
      fontSize: 11,
      fontWeight: 500,
      lineHeight: "18px",
      backgroundColor: t.page,
      color: t.textSecondary,
      border: `1px solid ${t.line}`,
      whiteSpace: "nowrap",
      overflow: "hidden",
    },
    badgeText: {
      ...ellipsis,
    },
    statusBadge: {
      borderColor: "transparent",
      fontWeight: 700,
      maxWidth: 104,
    },
    statusOpen: {
      backgroundColor: t.accentTint.medium,
      color: t.accentHover,
    },
    statusPending: {
      backgroundColor: `${t.semantic.warning}18`,
      color: t.semantic.warning,
    },
    statusClosed: {
      backgroundColor: t.page,
      color: t.textSecondary,
      borderColor: t.line,
    },
    statusGroup: {
      backgroundColor: `${t.semantic.info}18`,
      color: t.semantic.info,
    },
    dot: {
      flex: "none",
      width: 6,
      height: 6,
      borderRadius: "50%",
      backgroundColor: t.textMuted,
    },
    moreBadge: {
      minWidth: 26,
      maxWidth: "none",
      padding: "0 5px",
      justifyContent: "center",
      color: t.accentHover,
      backgroundColor: t.accentTint.soft,
      borderColor: "transparent",
      fontWeight: 700,
    },

    actions: {
      position: "absolute",
      right: 8,
      bottom: 7,
      display: "flex",
      alignItems: "center",
      gap: 2,
      opacity: 0,
      transform: "translateX(4px)",
      transition: "opacity 150ms ease, transform 150ms ease",
      [theme.breakpoints.down("sm")]: {
        opacity: 1,
        transform: "none",
      },
      "@media (hover: none), (pointer: coarse)": {
        opacity: 1,
        transform: "none",
      },
    },
    actionBtn: {
      minWidth: 0,
      width: 24,
      height: 24,
      padding: 0,
      borderRadius: 9,
      color: t.textMuted,
      backgroundColor: "transparent",
      boxShadow: "none",
      "& svg": {
        fontSize: 17,
      },
      "&:hover": {
        backgroundColor: t.accentTint.soft,
        boxShadow: t.shadow.card,
      },
    },
    actionAccept: {
      color: t.semantic.success,
    },
    actionTransfer: {
      color: t.accentHover,
    },
    actionClose: {
      color: t.semantic.error,
    },
    actionSpy: {
      color: t.textSecondary,
    },

    dialogButton: {
      background: t.brand,
      color: t.brandOn,
      "&:hover": {
        background: t.brandHover,
      },
    },

    // Estilos para o modal da imagem
    imageModal: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    imageModalContent: {
      outline: "none",
      maxWidth: "90vw",
      maxHeight: "90vh",
    },
    expandedImage: {
      width: "100%",
      height: "auto",
      maxWidth: "500px",
      borderRadius: t.radius.sm,
    },
  };
});

const TicketListItemCustom = ({ setTabOpen, ticket }) => {
  const classes = useStyles();
  const history = useHistory();
  const sla = useTicketSla(ticket);
  const { isMuted } = useMutes();
  const muteContactId = ticket.contactId || ticket.contact?.id;
  const muted = isMuted(muteContactId);
  const [muteMenuPos, setMuteMenuPos] = useState(null);
  const [muteDialogOpen, setMuteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [
    acceptTicketWithouSelectQueueOpen,
    setAcceptTicketWithouSelectQueueOpen,
  ] = useState(false);
  const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);

  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");

  // Estados para o modal de finalização de venda
  const [openFinalizacaoVenda, setOpenFinalizacaoVenda] = useState(false);
  const [finalizacaoTipo, setFinalizacaoTipo] = useState(null);
  const [ticketDataToFinalize, setTicketDataToFinalize] = useState(null);
  const [showFinalizacaoOptions, setShowFinalizacaoOptions] = useState(false);

  const [imageModalOpen, setImageModalOpen] = useState(false); // Estado para o modal da imagem

  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { setCurrentTicket } = useContext(TicketsContext);
  const { user } = useContext(AuthContext);

  const { get: getSetting } = useCompanySettings();

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Função para abrir modal da imagem
  const handleImageClick = (e) => {
    e.stopPropagation(); // Prevenir que o clique no avatar selecione o ticket
    if (ticket?.contact?.urlPicture) {
      setImageModalOpen(true);
    }
  };

  // Função para fechar modal da imagem
  const handleImageModalClose = () => {
    setImageModalOpen(false);
  };

  const handleOpenAcceptTicketWithouSelectQueue = useCallback(() => {
    setAcceptTicketWithouSelectQueueOpen(true);
  }, []);

  const handleCloseTicket = async (id) => {
    // Verificar se a finalização com valor de venda está ativa
    if (
      user.finalizacaoComValorVendaAtiva === true ||
      user.finalizacaoComValorVendaAtiva === "true"
    ) {
      // Se estiver ativa, abrir o modal de finalização de venda
      setFinalizacaoTipo("comDespedida");
      setOpenFinalizacaoVenda(true);
      handleSelectTicket(ticket);
      history.push(`/tickets/${ticket.uuid}`);
    } else {
      // Comportamento original
      const setting = await getSetting({
        column: "requiredTag",
      });

      if (setting.requiredTag === "enabled") {
        //verificar se tem uma tag
        try {
          const contactTags = await api.get(
            `/contactTags/${ticket.contact.id}`
          );
          if (!contactTags.data.tags) {
            toast.warning(i18n.t("messagesList.header.buttons.requiredTag"));
          } else {
            await api.put(`/tickets/${id}`, {
              status: "closed",
              userId: user?.id || null,
            });

            if (isMounted.current) {
              setLoading(false);
            }

            history.push(`/tickets/`);
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      } else {
        setLoading(true);
        try {
          await api.put(`/tickets/${id}`, {
            status: "closed",
            userId: user?.id || null,
          });
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
        if (isMounted.current) {
          setLoading(false);
        }

        history.push(`/tickets/`);
      }
    }
  };

  const handleCloseIgnoreTicket = async (id) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: "closed",
        userId: user?.id || null,
        sendFarewellMessage: false,
        amountUsedBotQueues: 0,
      });
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
    if (isMounted.current) {
      setLoading(false);
    }

    history.push(`/tickets/`);
  };

  const truncate = (str, len) => {
    if (!isNil(str)) {
      if (str.length > len) {
        return str.substring(0, len) + "...";
      }
      return str;
    }
  };

  const handleCloseTransferTicketModal = useCallback(() => {
    if (isMounted.current) {
      setTransferTicketModalOpen(false);
    }
  }, []);

  const handleOpenTransferModal = () => {
    setLoading(true);
    setTransferTicketModalOpen(true);
    if (isMounted.current) {
      setLoading(false);
    }
    handleSelectTicket(ticket);
    history.push(`/tickets/${ticket.uuid}`);
  };

  const handleOpenNewTicketModal = () => {
    setNewTicketModalOpen(true);
  };

  const handleCloseNewTicketModal = (newTicket) => {
    setNewTicketModalOpen(false);
    if (newTicket) {
      // Se um novo ticket foi criado, redirecionar para ele
      handleSelectTicket(newTicket);
      history.push(`/tickets/${newTicket.uuid}`);
    }
  };

  const handleAcepptTicket = async (id) => {
    setLoading(true);
    try {
      const otherTicket = await api.put(`/tickets/${id}`, {
        status: "open",
        userId: user?.id,
      });

      if (otherTicket.data.id !== ticket.id) {
        if (otherTicket.data.userId !== user?.id) {
          setOpenAlert(true);
          setUserTicketOpen(otherTicket.data.user.name);
          setQueueTicketOpen(otherTicket.data.queue.name);
        } else {
          setLoading(false);
          setTabOpen(ticket.isGroup ? "group" : "open");
          handleSelectTicket(otherTicket.data);
          history.push(`/tickets/${otherTicket.uuid}`);
        }
      } else {
        let setting;

        try {
          setting = await getSetting({
            column: "sendGreetingAccepted",
          });
        } catch (err) {
          toastError(err);
        }

        if (
          setting.sendGreetingAccepted === "enabled" &&
          (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === "enabled")
        ) {
          handleSendMessage(ticket.id);
        }
        if (isMounted.current) {
          setLoading(false);
        }

        setTabOpen(ticket.isGroup ? "group" : "open");
        handleSelectTicket(ticket);
        history.push(`/tickets/${ticket.uuid}`);
      }
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  const handleSendMessage = async (id) => {
    let setting;

    try {
      setting = await getSetting({
        column: "greetingAcceptedMessage",
      });
    } catch (err) {
      toastError(err);
    }
    if (!setting.greetingAcceptedMessage) {
      toast.warning(
        i18n.t("messagesList.header.buttons.greetingAcceptedMessage")
      );
      return;
    }
    const msg = `${setting.greetingAcceptedMessage}`;
    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: `${msg.trim()}`,
    };
    try {
      await api.post(`/messages/${id}`, message);
    } catch (err) {
      toastError(err);
    }
  };

  const handleCloseAlert = useCallback(() => {
    setOpenAlert(false);
    setLoading(false);
  }, []);

  const handleSelectTicket = (ticket) => {
    const code = uuidv4();
    const { id, uuid } = ticket;
    setCurrentTicket({ id, uuid, code });
  };

  const handleUpdateTicketStatusWithData = async (
    ticketData,
    sendFarewellMessage,
    finalizacaoMessage
  ) => {
    try {
      await api.put(`/tickets/${ticket.id}`, {
        ...ticketData,
        sendFarewellMessage,
        finalizacaoMessage,
      });
      toast.success("Ticket finalizado com sucesso!");
      history.push(`/tickets/`);
    } catch (err) {
      toastError(err);
    }
  };

  // Função para espionar ticket chatbot
  const handleSpyTicket = () => {
    handleSelectTicket(ticket);
    history.push(`/tickets/${ticket.uuid}`);
  };

  // Lógica de permissão para mensagens pending - MOVIDA PARA DEPOIS DE TODAS AS FUNÇÕES
  const shouldBlurMessages = ticket.status === "pending" && user?.allowSeeMessagesInPendingTickets === "disabled";

  // Função para renderizar a mensagem com base na permissão - MOVIDA PARA DEPOIS DE TODAS AS FUNÇÕES
  const renderLastMessage = () => {
    if (shouldBlurMessages) {
      return (
        <MarkdownWrapper>
          {i18n.t("tickets.messageHidden") || "Mensagem oculta"}
        </MarkdownWrapper>
      );
    }

    if (!ticket.lastMessage) {
      return <br />;
    }

    if (ticket.lastMessage.includes("data:image/png;base64")) {
      return <MarkdownWrapper>Localização</MarkdownWrapper>;
    }

    if (ticket.lastMessage.includes("BEGIN:VCARD")) {
      return <MarkdownWrapper>Contato</MarkdownWrapper>;
    }

    return (
      <MarkdownWrapper>
        {truncate(ticket.lastMessage, 40)}
      </MarkdownWrapper>
    );
  };

  const hasUnread = Number(ticket.unreadMessages) > 0;

  const statusPresentation = {
    open: {
      label: i18n.t("ticketsList.status.open"),
      className: classes.statusOpen,
    },
    pending: {
      label: i18n.t("ticketsList.status.pending"),
      className: classes.statusPending,
    },
    closed: {
      label: i18n.t("ticketsList.status.closed"),
      className: classes.statusClosed,
    },
    group: {
      label: i18n.t("ticketsList.status.group"),
      className: classes.statusGroup,
    },
    chatbot: { label: "Chatbot", className: classes.statusGroup },
    lgpd: { label: "LGPD", className: classes.statusPending },
  }[ticket.status];

  // Tags, conexão e atendente ficam reunidos num contador com tooltip. Assim
  // nenhum chip termina cortado quando a coluna de conversas fica estreita.
  const hiddenAdditionalBadges = Array.from(
    new Set(
      [
        ...(ticket?.contact?.tags || []).map((tag) => tag.name),
        ...(ticket.tags || []).map((tag) => tag.name),
        ticket?.whatsapp?.name,
        ticket?.user?.name,
      ].filter(Boolean)
    )
  );

  return (
    <React.Fragment key={ticket.id}>
      {openAlert && (
        <ShowTicketOpen
          isOpen={openAlert}
          handleClose={handleCloseAlert}
          user={userTicketOpen}
          queue={queueTicketOpen}
        />
      )}
      {acceptTicketWithouSelectQueueOpen && (
        <AcceptTicketWithouSelectQueue
          modalOpen={acceptTicketWithouSelectQueueOpen}
          onClose={(e) => setAcceptTicketWithouSelectQueueOpen(false)}
          ticketId={ticket.id}
          ticket={ticket}
        />
      )}
      {transferTicketModalOpen && (
        <TransferTicketModalCustom
          modalOpen={transferTicketModalOpen}
          onClose={handleCloseTransferTicketModal}
          ticketid={ticket.id}
          ticket={ticket}
        />
      )}
      {newTicketModalOpen && (
        <NewTicketModal
          modalOpen={newTicketModalOpen}
          onClose={handleCloseNewTicketModal}
          initialContact={ticket.contact}
        />
      )}
      <ListItem
        button
        dense
        disableGutters
        onClick={(e) => {
          const isCheckboxClicked =
            (e.target.tagName.toLowerCase() === "input" &&
              e.target.type === "checkbox") ||
            (e.target.tagName.toLowerCase() === "svg" &&
              e.target.type === undefined) ||
            (e.target.tagName.toLowerCase() === "path" &&
              e.target.type === undefined);

          if (isCheckboxClicked) return;

          handleSelectTicket(ticket);
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          setMuteMenuPos({ top: e.clientY, left: e.clientX });
        }}
        selected={ticketId && ticketId === ticket.uuid}
        className={clsx(classes.ticket, {
          [classes.pendingTicket]: ticket.status === "pending",
          [classes.slaBreached]: !!(sla && sla.breached),
        })}
      >
        <div className={classes.avatarWrap}>
          <Avatar
            src={`${ticket?.contact?.urlPicture}`}
            className={classes.avatar}
            onClick={handleImageClick}
          />
          {ticket.channel && (
            <span className={classes.channelDot}>
              <ConnectionIcon connectionType={ticket.channel} />
            </span>
          )}
        </div>

        <div className={classes.body}>
          {/* linha 1: nome + horário */}
          <div className={classes.row}>
            <Typography
              component="span"
              className={classes.name}
              title={ticket.contact?.name}
            >
              {ticket.isGroup && ticket.channel === "whatsapp" && (
                <GroupIcon className={classes.groupIcon} />
              )}
              <span className={classes.nameText}>
                {truncate(ticket.contact?.name, 60)}
              </span>
            </Typography>
            <SlaChip ticket={ticket} sla={sla} />
            <MutedIcon contactId={muteContactId} className={classes.mutedIcon} />
            {ticket.lastMessage && (
              <span
                className={clsx(classes.time, {
                  [classes.timeUnread]: hasUnread,
                })}
              >
                {isSameDay(parseISO(ticket.updatedAt), new Date())
                  ? format(parseISO(ticket.updatedAt), "HH:mm")
                  : format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}
              </span>
            )}
          </div>

          {/* linha 2: última mensagem + não lidas */}
          <div className={classes.row}>
            <span
              className={clsx(classes.lastMessage, {
                [classes.lastMessageUnread]: hasUnread,
              })}
            >
              {renderLastMessage()}
            </span>
            {(shouldBlurMessages || hasUnread) && (
              <span
                className={clsx(classes.unread, {
                  [classes.unreadMuted]: muted,
                })}
              >
                {shouldBlurMessages ? "?" : ticket.unreadMessages}
              </span>
            )}
          </div>

          {/* linha 3: fila / conexão / atendente / tags + ações */}
          <div className={classes.row}>
            <span className={classes.meta}>
              {statusPresentation && (
                <span
                  className={clsx(
                    classes.badge,
                    classes.statusBadge,
                    statusPresentation.className
                  )}
                >
                  {statusPresentation.label}
                </span>
              )}
              <span
                className={classes.badge}
                title={
                  ticket.queueId
                    ? ticket.queue?.name
                    : i18n.t("momentsUser.noqueue")
                }
              >
                <span
                  className={classes.dot}
                  style={
                    ticket.queue?.color
                      ? { backgroundColor: ticket.queue.color }
                      : undefined
                  }
                />
                <span className={classes.badgeText}>
                  {ticket.queueId
                    ? ticket.queue?.name
                    : ticket.status === "lgpd"
                    ? "LGPD"
                    : `${i18n.t("momentsUser.noqueue")}`}
                </span>
              </span>
              {hiddenAdditionalBadges.length > 0 && (
                <span
                  className={clsx(classes.badge, classes.moreBadge)}
                  title={hiddenAdditionalBadges.join(", ")}
                >
                  +{hiddenAdditionalBadges.length}
                </span>
              )}
            </span>

            <span className={classes.actions}>
              {/* Para tickets com status chatbot, mostrar apenas o ícone de spy */}
              {ticket.status === "chatbot" && (
                <ButtonWithSpinner
                  className={clsx(classes.actionBtn, classes.actionSpy)}
                  size="small"
                  loading={loading}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSpyTicket();
                  }}
                >
                  <Tooltip title="Espiar conversa do chatbot">
                    <VisibilityIcon />
                  </Tooltip>
                </ButtonWithSpinner>
              )}

              {/* Para todos os outros status, manter os botões originais */}
              {ticket.status !== "chatbot" && (
                <>
                  {ticket.status === "pending" &&
                    (ticket.queueId === null ||
                      ticket.queueId === undefined) && (
                      <ButtonWithSpinner
                        className={clsx(classes.actionBtn, classes.actionAccept)}
                        size="small"
                        loading={loading}
                        onClick={(e) => handleOpenAcceptTicketWithouSelectQueue()}
                      >
                        <Tooltip title={`${i18n.t("ticketsList.buttons.accept")}`}>
                          <Done />
                        </Tooltip>
                      </ButtonWithSpinner>
                    )}
                  {ticket.status === "pending" && ticket.queueId !== null && (
                    <ButtonWithSpinner
                      className={clsx(classes.actionBtn, classes.actionAccept)}
                      size="small"
                      loading={loading}
                      onClick={(e) => handleAcepptTicket(ticket.id)}
                    >
                      <Tooltip title={`${i18n.t("ticketsList.buttons.accept")}`}>
                        <Done />
                      </Tooltip>
                    </ButtonWithSpinner>
                  )}
                  {(ticket.status === "pending" ||
                    ticket.status === "open" ||
                    ticket.status === "group") && (
                    <ButtonWithSpinner
                      className={clsx(classes.actionBtn, classes.actionTransfer)}
                      size="small"
                      loading={loading}
                      onClick={handleOpenTransferModal}
                    >
                      <Tooltip title={`${i18n.t("ticketsList.buttons.transfer")}`}>
                        <SwapHoriz />
                      </Tooltip>
                    </ButtonWithSpinner>
                  )}
                  {(ticket.status === "open" || ticket.status === "group") && (
                    <ButtonWithSpinner
                      className={clsx(classes.actionBtn, classes.actionClose)}
                      size="small"
                      loading={loading}
                      onClick={(e) => handleCloseTicket(ticket.id)}
                    >
                      <Tooltip title={`${i18n.t("ticketsList.buttons.closed")}`}>
                        <HighlightOff />
                      </Tooltip>
                    </ButtonWithSpinner>
                  )}
                  {(ticket.status === "pending" || ticket.status === "lgpd") &&
                    (user.userClosePendingTicket === "enabled" ||
                      user.profile === "admin") && (
                      <ButtonWithSpinner
                        className={clsx(classes.actionBtn, classes.actionClose)}
                        size="small"
                        loading={loading}
                        onClick={(e) => handleCloseIgnoreTicket(ticket.id)}
                      >
                        <Tooltip title={`${i18n.t("ticketsList.buttons.ignore")}`}>
                          <HighlightOff />
                        </Tooltip>
                      </ButtonWithSpinner>
                    )}
                  {ticket.status === "closed" && (
                    <ButtonWithSpinner
                      className={clsx(classes.actionBtn, classes.actionAccept)}
                      size="small"
                      loading={loading}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenNewTicketModal();
                      }}
                    >
                      <Tooltip title="Criar Novo Ticket">
                        <Add />
                      </Tooltip>
                    </ButtonWithSpinner>
                  )}
                </>
              )}
            </span>
          </div>
        </div>
      </ListItem>
      {muteMenuPos && (
        <Menu
          open
          keepMounted={false}
          onClose={() => setMuteMenuPos(null)}
          anchorReference="anchorPosition"
          anchorPosition={muteMenuPos}
        >
          <MuteMenuItem
            contactId={muteContactId}
            onMute={() => {
              setMuteMenuPos(null);
              setMuteDialogOpen(true);
            }}
            onDone={() => setMuteMenuPos(null)}
          />
        </Menu>
      )}
      {muteDialogOpen && (
        <MuteDialog
          open
          contactId={muteContactId}
          isGroup={!!(ticket.isGroup || ticket.contact?.isGroup)}
          onClose={() => setMuteDialogOpen(false)}
        />
      )}

      {/* Modal de Finalização de Venda */}
      {openFinalizacaoVenda && (
        <FinalizacaoVendaModal
          open={openFinalizacaoVenda}
          onClose={() => setOpenFinalizacaoVenda(false)}
          ticket={ticket}
          onFinalizar={(ticketData) => {
            setOpenFinalizacaoVenda(false);
            setTicketDataToFinalize(ticketData);
            setShowFinalizacaoOptions(true);
          }}
        />
      )}

      {/* Modal de Opções de Finalização */}
      {showFinalizacaoOptions && (
        <Dialog
          open={showFinalizacaoOptions}
          onClose={() => setShowFinalizacaoOptions(false)}
          aria-labelledby="finalizacao-options-title"
        >
          <DialogTitle id="finalizacao-options-title">
            Como deseja finalizar?
          </DialogTitle>
          <DialogActions>
            <Button
              onClick={async () => {
                setShowFinalizacaoOptions(false);
                await handleUpdateTicketStatusWithData(
                  ticketDataToFinalize,
                  false,
                  null
                );
              }}
              className={classes.dialogButton}
            >
              {i18n.t("messagesList.header.dialogRatingWithoutFarewellMsg")}
            </Button>
            <Button
              onClick={async () => {
                setShowFinalizacaoOptions(false);
                await handleUpdateTicketStatusWithData(
                  ticketDataToFinalize,
                  true,
                  null
                );
              }}
              className={classes.dialogButton}
            >
              {i18n.t("messagesList.header.dialogRatingCancel")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal da Imagem */}
      <Dialog
        open={imageModalOpen}
        onClose={handleImageModalClose}
        className={classes.imageModal}
        maxWidth="md"
        fullWidth
      >
        <DialogContent className={classes.imageModalContent}>
          <img
            src={ticket?.contact?.urlPicture}
            alt={ticket?.contact?.name || "Foto do contato"}
            className={classes.expandedImage}
          />
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default TicketListItemCustom;
